/**
 * 全屏图片预览组件。
 *
 * 用 React Portal 渲染到 document.body，避免被祖先的 transform / filter /
 * backdrop-filter 等创建的 containing block 限制定位（否则 `fixed inset-0`
 * 会变成相对祖先而非 viewport，导致预览图不居中、只覆盖局部）。
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

interface LightboxProps {
  image: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function Lightbox({ image, isOpen, onClose }: LightboxProps) {
  // Esc 关闭
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || !image) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label="退出预览"
          onClick={onClose}
          className="absolute -top-3 -right-3 w-10 h-10 bg-black/75 hover:bg-black text-white rounded-full flex items-center justify-center shadow-xl ring-1 ring-white/30 transition-colors z-[10001]"
        >
          <X className="w-6 h-6" />
        </button>
        <img
          src={resolveBrowserMediaUrl(image)}
          alt="Generated"
          className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl shadow-2xl"
        />
      </div>
    </div>,
    document.body
  );
}
